import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  HStack,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';

import {
  applyCopyFromExternalListingApi,
  previewExternalListingForSkuApi,
} from 'features/sku-management/sku-management.api';

const FITMENT_COLS = ['Year', 'Make', 'Model', 'Trim', 'Engine', 'Notes'];

/** eBay / enrichment rows may use different key casing. */
const fitmentCellValue = (row, col) => {
  if (!row || typeof row !== 'object') {
    return '—';
  }
  if (row[col] != null && String(row[col]).trim() !== '') {
    return String(row[col]).trim();
  }
  const target = col.toLowerCase();
  for (const [k, v] of Object.entries(row)) {
    if (k.toLowerCase() === target && v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return '—';
};

const CopyFromListingModal = ({
  isOpen,
  onClose,
  row,
  onAfterApply,
  borderColor,
  cardBg,
  mutedTextColor,
  textColor,
  inputColor,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();

  const [step, setStep] = useState('input');
  const [itemIdInput, setItemIdInput] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);

  const [copyTitle, setCopyTitle] = useState(false);
  const [copyFitment, setCopyFitment] = useState(false);
  const [replaceAllImages, setReplaceAllImages] = useState(false);
  const [selectedImageUrls, setSelectedImageUrls] = useState(new Set());
  const [selectedSpecKeys, setSelectedSpecKeys] = useState(new Set());

  const resetState = () => {
    setStep('input');
    setItemIdInput('');
    setPreview(null);
    setCopyTitle(false);
    setCopyFitment(false);
    setReplaceAllImages(false);
    setSelectedImageUrls(new Set());
    setSelectedSpecKeys(new Set());
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const loadPreview = async () => {
    if (!row?.id || !itemIdInput.trim()) {
      return;
    }
    setLoadingPreview(true);
    try {
      const data = await dispatch(
        previewExternalListingForSkuApi({
          skuMongoId: row.id,
          ebayItemId: itemIdInput.trim(),
        }),
      ).unwrap();

      setPreview(data);
      setCopyTitle(false);
      setCopyFitment(false);
      setReplaceAllImages(false);
      setSelectedImageUrls(new Set());
      setSelectedSpecKeys(new Set());
      setStep('preview');
    } catch (e) {
      toast({
        title: 'Could not load listing',
        description:
          e?.payload?.message || e?.message || 'Check the item ID and try again.',
        status: 'error',
        duration: 6000,
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleImage = (url) => {
    setSelectedImageUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const toggleAllImages = (checked) => {
    if (!preview?.images) {
      return;
    }
    setSelectedImageUrls(checked ? new Set(preview.images) : new Set());
  };

  const toggleSpec = (key) => {
    setSelectedSpecKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const allImagesSelected =
    preview?.images?.length > 0 &&
    preview.images.every((u) => selectedImageUrls.has(u));

  const selectedImagesOrdered = useMemo(() => {
    if (!preview?.images) {
      return [];
    }
    return preview.images.filter((u) => selectedImageUrls.has(u));
  }, [preview, selectedImageUrls]);

  const selectedSpecs = useMemo(() => {
    if (!preview?.specifications) {
      return [];
    }
    return preview.specifications.filter((s) => selectedSpecKeys.has(s.key));
  }, [preview, selectedSpecKeys]);

  const canApply =
    copyTitle ||
    copyFitment ||
    selectedImagesOrdered.length > 0 ||
    selectedSpecs.length > 0;

  const handleApply = async () => {
    if (!row?.id || !canApply) {
      return;
    }
    setApplying(true);
    try {
      const body = {};
      if (copyTitle && preview?.title) {
        body.title = preview.title;
      }
      if (selectedImagesOrdered.length > 0) {
        body.images = selectedImagesOrdered;
        body.replaceImages = replaceAllImages;
      }
      if (selectedSpecs.length > 0) {
        body.specifications = selectedSpecs;
      }
      if (copyFitment && preview?.fitment?.length) {
        body.fitment = preview.fitment;
      }

      const data = await dispatch(
        applyCopyFromExternalListingApi({ skuMongoId: row.id, body }),
      ).unwrap();

      const results = data?.listingResults || [];
      const failed = results.filter((r) => !r.success).length;
      toast({
        title: data?.message || 'Copy applied',
        description:
          results.length === 0
            ? 'Catalog updated (no linked listings to push).'
            : failed > 0
              ? `${results.length - failed} of ${results.length} eBay listing(s) updated.`
              : `Updated catalog and ${results.length} linked listing(s).`,
        status: failed > 0 ? 'warning' : 'success',
        duration: 6000,
      });
      handleClose();
      onAfterApply?.();
    } catch (e) {
      toast({
        title: 'Update failed',
        description:
          e?.payload?.message || e?.message || 'Could not copy data to catalog',
        status: 'error',
      });
    } finally {
      setApplying(false);
    }
  };

  const ebayListingUrl = preview?.ebayItemId
    ? `https://www.ebay.com/itm/${preview.ebayItemId}`
    : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="min(1200px, 96vw)">
        <ModalHeader pr={14}>
          Copy from other listing — {row?.canonicalSku}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          {step === 'input' && (
            <VStack align="stretch" spacing={4} maxW="md">
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  Enter an eBay item ID. We load the live listing via GetItem using your linked
                  eBay store credentials, then you choose which fields to copy to this SKU.
                </Text>
              </Alert>
              <FormControl>
                <FormLabel>eBay item ID</FormLabel>
                <Input
                  placeholder="e.g. 123456789012"
                  value={itemIdInput}
                  onChange={(e) => setItemIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      loadPreview();
                    }
                  }}
                  color={inputColor}
                />
              </FormControl>
              <Button
                colorScheme="blue"
                onClick={loadPreview}
                isLoading={loadingPreview}
                alignSelf="flex-start"
              >
                Load listing
              </Button>
            </VStack>
          )}

          {step === 'preview' && preview && (
            <Box>
              <Box
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="lg"
                overflow="hidden"
                bg={cardBg}
              >
                <Box bg="gray.50" px={6} py={4} borderBottomWidth="1px" borderColor={borderColor}>
                  <Text fontSize="xs" color={mutedTextColor} mb={1}>
                    {preview.categoryName || 'eBay Motors'}
                    {preview.categoryId ? ` · ID ${preview.categoryId}` : ''}
                  </Text>
                  <Flex justify="space-between" align="start" gap={4} flexWrap="wrap">
                    <Box flex="1" minW="200px">
                      <HStack mb={2} align="center">
                        <Checkbox
                          isChecked={copyTitle}
                          onChange={(e) => setCopyTitle(e.target.checked)}
                          colorScheme="blue"
                        />
                        <Text fontSize="xs" fontWeight="semibold" color="blue.600">
                          Copy title
                        </Text>
                      </HStack>
                      <Text fontSize="lg" fontWeight="semibold" color={textColor} lineHeight="short">
                        {preview.title || '—'}
                      </Text>
                    </Box>
                    <VStack align="end" spacing={1}>
                      {preview.priceDisplay ? (
                        <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                          {preview.currency === 'USD' ? '$' : ''}
                          {preview.priceDisplay}
                        </Text>
                      ) : null}
                      {ebayListingUrl ? (
                        <Link href={ebayListingUrl} isExternal fontSize="sm" color="blue.500">
                          View on eBay
                        </Link>
                      ) : null}
                    </VStack>
                  </Flex>
                  <HStack mt={2} spacing={4} fontSize="sm" color={mutedTextColor} flexWrap="wrap">
                    {preview.conditionDisplayName ? (
                      <Text>Condition: {preview.conditionDisplayName}</Text>
                    ) : null}
                    {preview.brand ? <Text>Brand: {preview.brand}</Text> : null}
                    {preview.itemLocation ? <Text>Location: {preview.itemLocation}</Text> : null}
                    <Text>Item {preview.ebayItemId}</Text>
                  </HStack>
                </Box>

                <Box px={6} py={5}>
                  <Box
                    mb={4}
                    p={3}
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    bg="gray.50"
                  >
                    <Checkbox
                      isChecked={replaceAllImages}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setReplaceAllImages(checked);
                        if (checked && preview.images?.length) {
                          setSelectedImageUrls(new Set(preview.images));
                        }
                      }}
                      colorScheme="blue"
                    >
                      <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                        Replace all catalog images
                      </Text>
                    </Checkbox>
                    <Text fontSize="xs" color={mutedTextColor} mt={2} ml={6}>
                      {replaceAllImages
                        ? 'Checked: only the images you select below will remain (previous catalog images are removed).'
                        : 'Unchecked: selected images are added to your existing catalog images.'}
                    </Text>
                  </Box>

                  <HStack mb={3} justify="space-between" flexWrap="wrap">
                    <HStack>
                      <Checkbox
                        isChecked={allImagesSelected && preview.images?.length > 0}
                        isIndeterminate={
                          selectedImageUrls.size > 0 &&
                          !allImagesSelected &&
                          preview.images?.length > 0
                        }
                        onChange={(e) => toggleAllImages(e.target.checked)}
                        colorScheme="blue"
                      />
                      <Text fontWeight="semibold" color={textColor}>
                        Images from listing ({selectedImagesOrdered.length}/
                        {preview.images?.length ?? 0} selected)
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color={mutedTextColor}>
                      {replaceAllImages
                        ? 'First selected image below becomes main'
                        : 'New images append after your current catalog images'}
                    </Text>
                  </HStack>
                  {preview.images?.length ? (
                    <SimpleGrid columns={{ base: 3, md: 5, lg: 6 }} spacing={3} mb={6}>
                      {preview.images.map((url, idx) => (
                        <Box
                          key={url}
                          borderWidth="2px"
                          borderColor={selectedImageUrls.has(url) ? 'blue.400' : borderColor}
                          borderRadius="md"
                          overflow="hidden"
                          position="relative"
                          cursor="pointer"
                          onClick={() => toggleImage(url)}
                          bg="white"
                        >
                          <Checkbox
                            position="absolute"
                            top={2}
                            left={2}
                            zIndex={1}
                            isChecked={selectedImageUrls.has(url)}
                            onChange={() => toggleImage(url)}
                            colorScheme="blue"
                            bg="whiteAlpha.900"
                            borderRadius="sm"
                            onClick={(e) => e.stopPropagation()}
                          />
                          {idx === 0 && selectedImageUrls.has(url) ? (
                            <Text
                              position="absolute"
                              bottom={1}
                              left="50%"
                              transform="translateX(-50%)"
                              fontSize="2xs"
                              bg="gray.700"
                              color="white"
                              px={2}
                              py={0.5}
                              borderRadius="sm"
                              zIndex={1}
                            >
                              Main
                            </Text>
                          ) : null}
                          <Image
                            src={url}
                            alt=""
                            w="100%"
                            h="100px"
                            objectFit="contain"
                          />
                        </Box>
                      ))}
                    </SimpleGrid>
                  ) : (
                    <Text fontSize="sm" color={mutedTextColor} mb={6}>
                      No images on this listing.
                    </Text>
                  )}

                  <Divider my={4} />

                  <Text fontWeight="semibold" color={textColor} mb={2}>
                    Item specifics ({selectedSpecs.length}/{preview.specifications?.length ?? 0}{' '}
                    selected)
                  </Text>
                  <Text fontSize="xs" color={mutedTextColor} mb={3}>
                    Select only the rows you want; seller-specific values are skipped by leaving
                    them unchecked.
                  </Text>
                  <Box
                    maxH="220px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    mb={6}
                  >
                    <Table size="sm">
                      <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                        <Tr>
                          <Th w="40px" />
                          <Th>Key</Th>
                          <Th>Value</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(preview.specifications || []).length === 0 ? (
                          <Tr>
                            <Td colSpan={3}>
                              <Text fontSize="sm" color={mutedTextColor}>
                                No item specifics.
                              </Text>
                            </Td>
                          </Tr>
                        ) : (
                          preview.specifications.map((spec) => (
                            <Tr key={spec.key}>
                              <Td>
                                <Checkbox
                                  isChecked={selectedSpecKeys.has(spec.key)}
                                  onChange={() => toggleSpec(spec.key)}
                                  colorScheme="blue"
                                />
                              </Td>
                              <Td fontWeight="medium">{spec.key}</Td>
                              <Td>{spec.value}</Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>

                  <Divider my={4} />

                  <HStack mb={3}>
                    <Checkbox
                      isChecked={copyFitment}
                      onChange={(e) => setCopyFitment(e.target.checked)}
                      colorScheme="blue"
                    />
                    <Text fontWeight="semibold" color={textColor}>
                      Copy fitment ({preview.fitment?.length ?? 0} rows — replaces catalog
                      fitment entirely)
                    </Text>
                  </HStack>
                  <TableContainer
                    maxH="380px"
                    overflowY="auto"
                    overflowX="auto"
                    borderWidth="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                  >
                    <Table size="sm" variant="simple" layout="fixed" minW="720px">
                      <Thead position="sticky" top={0} bg="gray.50" zIndex={1}>
                        <Tr>
                          {FITMENT_COLS.map((c) => (
                            <Th
                              key={c}
                              fontSize="xs"
                              whiteSpace="nowrap"
                              px={2}
                              w={c === 'Notes' ? '22%' : c === 'Trim' || c === 'Engine' ? '18%' : '10%'}
                            >
                              {c.toUpperCase()}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(preview.fitment || []).length === 0 ? (
                          <Tr>
                            <Td colSpan={FITMENT_COLS.length}>
                              <Text fontSize="sm" color={mutedTextColor}>
                                No fitment on this listing.
                              </Text>
                            </Td>
                          </Tr>
                        ) : (
                          preview.fitment.map((f, i) => (
                            <Tr key={i}>
                              {FITMENT_COLS.map((col) => (
                                <Td
                                  key={col}
                                  fontSize="xs"
                                  px={2}
                                  py={2}
                                  verticalAlign="top"
                                  whiteSpace="normal"
                                  wordBreak="break-word"
                                >
                                  {fitmentCellValue(f, col)}
                                </Td>
                              ))}
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </Box>
          )}

          {loadingPreview && step === 'input' ? (
            <Flex justify="center" py={8}>
              <Spinner />
            </Flex>
          ) : null}
        </ModalBody>

        <ModalFooter justifyContent="space-between" flexWrap="wrap" gap={2}>
          <HStack spacing={2}>
            {step === 'preview' ? (
              <>
                <Button
                  colorScheme="blue"
                  isLoading={applying}
                  isDisabled={!canApply}
                  onClick={handleApply}
                >
                  Update catalog &amp; listings
                </Button>
                <Button variant="ghost" onClick={() => setStep('input')}>
                  Back
                </Button>
              </>
            ) : null}
          </HStack>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CopyFromListingModal;
